import { AppSelect } from './ui/AppSelect';
import React, { useState, useEffect, useRef } from 'react';
import { Wand2, Sparkles, Upload, X, Palette, Trash2, Edit2, Check, Plus, Loader2, RefreshCw, ImagePlus, PackagePlus, ChevronRight, Download } from 'lucide-react';
import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';
import { runImageTaskToUrl } from '../services/taskService';
import { STYLE_OPTIONS } from "./storyboard/common/Constants";
import { useTheme } from "../context/ThemeContext";
import { useStyle } from "../context/StyleContext";
import { useToast } from "../context/ToastContext";
import { ConfirmDialog } from '@ui';
import AssetRenameDialog from "./assets/AssetRenameDialog";
import WorkshopVariantUpload from './assets/WorkshopVariantUpload';
import WorkshopArtwork from "./ui/WorkshopArtwork";
import "./assets/WorkshopShell.css";
import { ModelArkImageLibraryStatus, ModelArkLibraryToolbar } from './assets/ModelArkLibraryControls';
import { useModelArkAssets } from '../hooks/useModelArkAssets';
import { collectWorkshopModelArkReferences } from '../utils/modelArkAssets';
import { applyAssetCreateResult, applyAssetStatusResult, assetLibraryLabel, assetStatusLabel, clearVideoAssetBinding, getEffectiveAssetMeta, getVideoAssetFieldNames, isAssetReadyStatus } from "../utils/guaVideoAssetStatus";
import { getProjectVideoAssetApiBase, getProjectVideoProvider, supportsVideoAssetIngest } from "../utils/projectRuntime";
import { useWorkshopImageSettings } from "../hooks/useWorkshopImageSettings";
import { createWorkshopAssetSaveQueue, mergeWorkshopAssetRegistration, uploadWorkshopVariant, validateVariantImageFile } from '../utils/workshopVariantUpload';
import {
  WANX_IMAGE_MAX_INPUT_IMAGES,
  assertWanxImageInputLimit,
  isWanxImageProvider,
  normalizeWanxImageQualityTier,
} from "../utils/wanxImageCapabilities";
import {
  isLatestWorkshopListRequest,
  isSameWorkshopAssetId,
  mergeWorkshopAssetById,
  nextWorkshopListRequestSequence,
} from "../utils/workshopAssetState";

const PropGenerator = ({ projectId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const { currentStyle, setCurrentStyle } = useStyle();
  const toast = useToast();
  const assetProvider = getProjectVideoProvider(projectId);
  const assetApiBase = getProjectVideoAssetApiBase(projectId);
  const assetIngestSupported = supportsVideoAssetIngest(assetProvider);
  const { imageProvider, imageQualityTier, availableImageQualityTierOptions, handleImageQualityTierChange } = useWorkshopImageSettings();
  const isWanxImage = isWanxImageProvider(imageProvider);
  const maxReferenceImages = isWanxImage ? WANX_IMAGE_MAX_INPUT_IMAGES : 5;

  const runWorkshopImageTaskToUrl = (taskType, payload, options = {}) => {
    const imageInputs = [
      payload?.baseImage,
      payload?.baseImageUrl,
      payload?.refImageUrl,
      ...(Array.isArray(payload?.referenceImages) ? payload.referenceImages : []),
    ].filter(Boolean);
    assertWanxImageInputLimit(imageInputs, imageProvider);
    return runImageTaskToUrl(taskType, {
      ...payload,
      imageQualityTier: normalizeWanxImageQualityTier(payload?.imageQualityTier, imageProvider),
    }, {
      ...options,
      provider: imageProvider,
    });
  };

  // 道具列表和选中状态
  const [props, setProps] = useState([]);
  const modelArkRegistry = useModelArkAssets({ projectId, projectProvider: assetProvider, references: collectWorkshopModelArkReferences(props) });
  const [selectedPropId, setSelectedPropId] = useState(null);

  // 新建道具弹窗
  const [isCreating, setIsCreating] = useState(false);
  const [newPropName, setNewPropName] = useState("");
  const [newPropPrompt, setNewPropPrompt] = useState("");
  const [newPropAliases, setNewPropAliases] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]); // [{url, label}]
  const [uploadingRefIndex, setUploadingRefIndex] = useState(null);

  // 别名编辑
  const [editingAlias, setEditingAlias] = useState(false);
  const [editAliasText, setEditAliasText] = useState("");

  // 描述编辑
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescriptionText, setEditDescriptionText] = useState("");

  // 按道具 id 追踪 loading 状态，避免切换道具时状态错位（使用 Set 支持多资源并发）
  const [regeneratingMainIds, setRegeneratingMainIds] = useState(new Set());
  const [replacingMainIds, setReplacingMainIds] = useState(new Set());
  const [generatingVariantIds, setGeneratingVariantIds] = useState(new Set());
  const [regeneratingVariantKeys, setRegeneratingVariantKeys] = useState(new Set()); // "propId-variantIndex"
  const [refiningIds, setRefiningIds] = useState(new Set());
  const [uploadingAssetIds, setUploadingAssetIds] = useState(new Set());

  // 变体生成
  const [showVariantPanel, setShowVariantPanel] = useState(false);
  const [variantMode, setVariantMode] = useState("text"); // "text" | "image"
  const [variantName, setVariantName] = useState("");
  const [variantPrompt, setVariantPrompt] = useState("");
  const [variantRefImage, setVariantRefImage] = useState(null);
  const [variantRefLabel, setVariantRefLabel] = useState(""); // 参考图用途说明
  const [deleteVariantIndex, setDeleteVariantIndex] = useState(null);

  // 变体展开（手风琴）
  const [expandedVariantIndex, setExpandedVariantIndex] = useState(null);
  const [variantRefinePrompt, setVariantRefinePrompt] = useState("");
  const [showVariantRefinePanel, setShowVariantRefinePanel] = useState(false);
  const [editingVariantDesc, setEditingVariantDesc] = useState(false);
  const [editVariantDescText, setEditVariantDescText] = useState("");
  const variantReplaceInputRefs = useRef(new Map());
  const variantRefinePromptTextareaRef = useRef(null);
  const editVariantDescTextareaRef = useRef(null);

  // 微调模式
  const [showRefinePanel, setShowRefinePanel] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState("");

  // 详情页参考图编辑
  const [editingDetailRefs, setEditingDetailRefs] = useState(false);
  const [detailRefImages, setDetailRefImages] = useState([]);
  const [uploadingDetailRefIndex, setUploadingDetailRefIndex] = useState(null);
  const detailRefInputRefs = useRef([]);

  // 其他
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const variantRefInputRef = useRef(null);
  const replaceMainInputRef = useRef(null);
  const refImageInputRefs = useRef([]);
  const pollingIdsRef = useRef(new Map());
  const pollingTimersRef = useRef(new Map());
  const propsRef = useRef([]);
  const assetSaveQueueRef = useRef(null);
  if (!assetSaveQueueRef.current) assetSaveQueueRef.current = createWorkshopAssetSaveQueue();
  const currentProjectIdRef = useRef(projectId);
  currentProjectIdRef.current = projectId;
  const propListRequestSequenceRef = useRef(0);
  const API_BASE = API_HOST;
  const createLocalToken = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const stopAssetPolling = (entityId) => {
    const timeoutId = pollingTimersRef.current.get(entityId);
    if (timeoutId != null) window.clearTimeout(timeoutId);
    pollingTimersRef.current.delete(entityId);
    pollingIdsRef.current.delete(entityId);
  };

  const scheduleAssetPoll = (entityId, token, poll, delayMs) => {
    if (pollingIdsRef.current.get(entityId) !== token) return;
    const previousTimeoutId = pollingTimersRef.current.get(entityId);
    if (previousTimeoutId != null) window.clearTimeout(previousTimeoutId);
    const timeoutId = window.setTimeout(() => {
      pollingTimersRef.current.delete(entityId);
      if (pollingIdsRef.current.get(entityId) === token) poll();
    }, delayMs);
    pollingTimersRef.current.set(entityId, timeoutId);
  };

  const stopAllAssetPolling = () => {
    pollingTimersRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
    pollingTimersRef.current.clear();
    pollingIdsRef.current.clear();
  };

  const savePropAsset = async (asset, { shouldApply = () => true, rebase } = {}) => {
    return assetSaveQueueRef.current.run(asset.id ?? "new", async () => {
      const latest = getPropById(asset.id);
      if (!shouldApply()) return latest;
      if (rebase && !latest) throw new Error("原道具已删除或已切换项目，未保存");
      const payload = rebase ? rebase(latest) : asset;
      const saved = await authenticatedFetch(`${API_BASE}/api/props`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (saved?.id != null && shouldApply()) {
        propListRequestSequenceRef.current = nextWorkshopListRequestSequence(propListRequestSequenceRef.current);
        propsRef.current = mergeWorkshopAssetById(propsRef.current, saved);
        setProps(prev => mergeWorkshopAssetById(prev, saved));
      }
      return saved;
    });
  };

  const getPropById = (id) => propsRef.current.find(prop => String(prop.id) === String(id));

  useEffect(() => {
    if (projectId) fetchProps();
  }, [projectId]);

  useEffect(() => () => {
    stopAllAssetPolling();
    propListRequestSequenceRef.current = nextWorkshopListRequestSequence(propListRequestSequenceRef.current);
  }, [projectId, assetProvider]);

  useEffect(() => { propsRef.current = props; }, [props]);

  const fetchProps = async ({ throwOnError = false } = {}) => {
    const requestSequence = nextWorkshopListRequestSequence(propListRequestSequenceRef.current);
    propListRequestSequenceRef.current = requestSequence;
    try {
      const data = await authenticatedFetch(`${API_BASE}/api/props?projectId=${projectId}`);
      const list = data || [];
      if (!isLatestWorkshopListRequest(requestSequence, propListRequestSequenceRef.current)) {
        return { applied: false, stale: true, list };
      }
      console.log('[PropGenerator] Fetched props:', list);
      propsRef.current = list;
      setProps(list);
      if (list.length > 0) setSelectedPropId(currentId => currentId ?? list[0].id);
      list.forEach(p => {
        const hasProcessing = getEffectiveAssetMeta(p, assetProvider).status === "Processing"
          || (p.variations || []).some(v => getEffectiveAssetMeta(v, assetProvider).status === "Processing");
        if (hasProcessing) pollAssetStatus(p);
      });
      return { applied: true, stale: false, list };
    } catch (e) {
      if (isLatestWorkshopListRequest(requestSequence, propListRequestSequenceRef.current)) {
        console.error(e);
      }
      if (throwOnError) throw e;
      return { applied: false, stale: false, error: e };
    }
  };

  const recoverPropImageAfterTaskError = async (propId, previousImageUrl = null) => {
    const refreshed = await fetchProps();
    if (!refreshed?.applied) return false;
    const asset = refreshed.list.find(item => isSameWorkshopAssetId(item.id, propId));
    const currentUrl = String(asset?.imageUrl || "").trim();
    return Boolean(currentUrl && currentUrl !== String(previousImageUrl || "").trim());
  };

  const selectedProp = props.find(p => isSameWorkshopAssetId(p.id, selectedPropId));
  const propAliases = selectedProp ? (() => { try { return JSON.parse(selectedProp.aliases || '[]'); } catch { return []; } })() : [];
  const propVariations = selectedProp?.variations || [];

  // 派生当前道具的 loading 状态
  const isRegeneratingMain = regeneratingMainIds.has(selectedPropId);
  const isReplacingMain = replacingMainIds.has(selectedPropId);
  const isGeneratingVariant = generatingVariantIds.has(selectedPropId);
  const isRefiningMain = refiningIds.has(selectedPropId);
  const getRegeneratingVariantIndex = (idx) => regeneratingVariantKeys.has(`${selectedPropId}-${idx}`);

  console.log('[PropGenerator] Selected prop:', selectedProp);
  console.log('[PropGenerator] Prop variations:', propVariations);

  // 校验新建道具表单
  const validateNewPropForm = () => {
    if (!newPropName.trim()) {
      toast.warning("请填写道具名称");
      return false;
    }
    if (!newPropAliases.trim()) {
      toast.warning("请填写道具别名");
      return false;
    }
    if (!newPropPrompt.trim()) {
      toast.warning("请填写道具描述");
      return false;
    }
    return true;
  };

  // 创建无图道具
  const createPropDraft = async () => {
    const aliasArray = newPropAliases.split(/[,，]/).map(s => s.trim()).filter(Boolean);
    return await savePropAsset({
      projectId,
      name: newPropName,
      description: newPropPrompt,
      imageUrl: null,
      aliases: JSON.stringify(aliasArray),
      referenceImages: referenceImages.length > 0 ? JSON.stringify(referenceImages) : null,
      variations: []
    });
  };

  // 上传参考图（新建弹窗）
  const handleUploadRefImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (referenceImages.length >= maxReferenceImages) {
      toast.warning(`当前图片模型最多支持${maxReferenceImages}张参考图`);
      e.target.value = "";
      return;
    }
    setUploadingRefIndex(referenceImages.length);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      setReferenceImages(prev => [...prev, { url: imageUrl, label: "" }]);
    } catch (err) {
      toast.error("上传失败: " + err.message);
    } finally {
      setUploadingRefIndex(null);
      e.target.value = "";
    }
  };

  // 删除参考图（新建弹窗）
  const handleRemoveRefImage = (index) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  };

  // 更新参考图标签
  const handleUpdateRefLabel = (index, label) => {
    setReferenceImages(prev => prev.map((ref, i) => i === index ? { ...ref, label } : ref));
  };

  // 详情页参考图上传
  const handleUploadDetailRefImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (detailRefImages.length >= maxReferenceImages) {
      toast.warning(`当前图片模型最多支持${maxReferenceImages}张参考图`);
      e.target.value = "";
      return;
    }
    setUploadingDetailRefIndex(detailRefImages.length);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      setDetailRefImages(prev => [...prev, { url: imageUrl, label: "" }]);
    } catch (err) {
      toast.error("上传失败: " + err.message);
    } finally {
      setUploadingDetailRefIndex(null);
      e.target.value = "";
    }
  };

  // 删除详情页参考图
  const handleRemoveDetailRefImage = (index) => {
    setDetailRefImages(prev => prev.filter((_, i) => i !== index));
  };

  // 更新详情页参考图标签
  const handleUpdateDetailRefLabel = (index, label) => {
    setDetailRefImages(prev => prev.map((ref, i) => i === index ? { ...ref, label } : ref));
  };

  // 开始编辑详情页参考图
  const handleStartEditDetailRefs = () => {
    const savedRefs = (() => { try { return JSON.parse(selectedProp.referenceImages || '[]'); } catch { return []; } })();
    // 兼容旧数据（纯URL字符串）
    const refs = savedRefs.map(r => typeof r === 'string' ? { url: r, label: '' } : r);
    setDetailRefImages(refs);
    setEditingDetailRefs(true);
  };

  // 保存详情页参考图
  const handleSaveDetailRefs = async () => {
    if (!selectedProp) return;
    try {
      await savePropAsset({ ...selectedProp, referenceImages: detailRefImages.length > 0 ? JSON.stringify(detailRefImages) : null });
      await fetchProps();
      setEditingDetailRefs(false);
      toast.success("参考图已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 新建道具 - AI生成并创建
  const handleGenerateNewMain = async () => {
    if (!validateNewPropForm()) return;
    try {
      assertWanxImageInputLimit(referenceImages, imageProvider);
    } catch (error) {
      toast.warning(error.message);
      return;
    }
    
    // 先创建无图道具
    setIsGenerating(true);
    let newProp;
    try {
      newProp = await createPropDraft();
      await fetchProps();
      setSelectedPropId(newProp.id);
      setIsCreating(false); // 关闭弹窗
    } catch (e) {
      toast.error("创建道具失败: " + e.message);
      setIsGenerating(false);
      return;
    }

    // 开始异步生成（复用重新生成的逻辑）
    const propId = newProp.id;
    const prompt = newPropPrompt;
    setRegeneratingMainIds(prev => new Set(prev).add(propId));

    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_PROP", {
        prompt,
        name: newProp.name,
        projectId,
        imageQualityTier,
        referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
        workshopAssetType: "prop",
        workshopAssetId: propId,
        workshopTarget: "main",
      }, { styleAnchor: currentStyle, projectId, source: "prop_workshop" });
      const latest = getPropById(propId) || newProp;
      await savePropAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchProps();
      // 清空表单
      setNewPropName(""); setNewPropPrompt(""); setNewPropAliases("");
      setReferenceImages([]);
      toast.success("道具生成成功！");
    } catch (e) {
      const recovered = await recoverPropImageAfterTaskError(propId);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("生成失败: " + e.message);
    } finally {
      setRegeneratingMainIds(prev => {
        const next = new Set(prev);
        next.delete(propId);
        return next;
      });
      setIsGenerating(false);
    }
  };

  // 新建道具 - 上传主图并创建
  const handleUploadNew = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 先校验表单
    if (!validateNewPropForm()) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    setIsUploading(true);
    
    // 上传图片
    const formData = new FormData();
    formData.append('file', file);
    let imageUrl;
    try {
      imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: 'POST', body: formData });
    } catch (err) { 
      toast.error("上传失败: " + err.message);
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    // 创建有图道具
    try {
      const aliasArray = newPropAliases.split(/[,，]/).map(s => s.trim()).filter(Boolean);
      const saved = await savePropAsset({
        projectId,
        name: newPropName,
        description: newPropPrompt,
        imageUrl: imageUrl,
        aliases: JSON.stringify(aliasArray),
        referenceImages: referenceImages.length > 0 ? JSON.stringify(referenceImages) : null,
        variations: []
      });
      await fetchProps();
      setSelectedPropId(saved?.id || null);
      setIsCreating(false);
      // 清空表单
      setNewPropName(""); setNewPropPrompt(""); setNewPropAliases("");
      setReferenceImages([]);
      toast.success("道具已创建！");
    } catch (e) {
      toast.error("保存失败: " + e.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };



  // 一键生成：为所有有描述但没图片的道具批量生图
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });

  const handleBatchGenerate = async () => {
    const pending = props.filter(p => p.description && p.description !== "道具图" && !p.imageUrl);
    if (pending.length === 0) return toast.info("所有道具都已有图片，无需生成");
    setBatchGenerating(true);
    setBatchProgress({ done: 0, total: pending.length });
    let successCount = 0;
    for (const prop of pending) {
      try {
        setRegeneratingMainIds(prev => new Set(prev).add(prop.id));
        const propRefImages = (() => { try { const r = JSON.parse(prop.referenceImages || '[]'); return r.map(x => typeof x === 'string' ? { url: x, label: '' } : x); } catch { return []; } })();
        const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_PROP", {
          prompt: prop.description,
          name: prop.name,
          projectId,
          imageQualityTier,
          referenceImages: propRefImages.length > 0 ? propRefImages : undefined,
          workshopAssetType: "prop",
          workshopAssetId: prop.id,
          workshopTarget: "main",
        }, { styleAnchor: currentStyle, projectId, source: "prop_workshop" });
        const latest = getPropById(prop.id) || prop;
        await savePropAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
        successCount++;
      } catch (e) {
        const recovered = await recoverPropImageAfterTaskError(prop.id, prop.imageUrl);
        if (recovered) {
          successCount++;
        } else {
          console.error(`[BatchGen] 道具 ${prop.name} 生成失败:`, e.message);
        }
        if (!recovered && isWanxImage && String(e.message || "").includes("千问图像3.0最多支持")) {
          toast.error(`${prop.name}：${e.message}`);
        }
      } finally {
        setRegeneratingMainIds(prev => { const next = new Set(prev); next.delete(prop.id); return next; });
        setBatchProgress(prev => ({ ...prev, done: prev.done + 1 }));
      }
    }
    await fetchProps();
    setBatchGenerating(false);
    toast.success(`一键生成完成：${successCount}/${pending.length} 个道具`);
  };

  // 主体图重新生成
  const handleRegenerateMain = async () => {
    if (!selectedProp) return;
    const propId = selectedProp.id;
    const prompt = selectedProp.description;
    if (!prompt || prompt === "道具图") return toast.warning("该道具没有描述，无法重新生成");
    setRegeneratingMainIds(prev => new Set(prev).add(propId));
    try {
      const propRefImages = (() => { try { const r = JSON.parse(selectedProp.referenceImages || '[]'); return r.map(x => typeof x === 'string' ? { url: x, label: '' } : x); } catch { return []; } })();
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_PROP", {
        prompt,
        name: selectedProp.name,
        projectId,
        imageQualityTier,
        referenceImages: propRefImages.length > 0 ? propRefImages : undefined,
        workshopAssetType: "prop",
        workshopAssetId: propId,
        workshopTarget: "main",
      }, { styleAnchor: currentStyle, projectId, source: "prop_workshop" });
      stopAssetPolling(propId);
      const latest = getPropById(propId) || selectedProp;
      await savePropAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchProps();
      toast.success("重新生成成功！");
    } catch (e) {
      const recovered = await recoverPropImageAfterTaskError(propId, selectedProp.imageUrl);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("生成失败: " + e.message);
    }
    finally {
      setRegeneratingMainIds(prev => {
        const next = new Set(prev);
        next.delete(propId);
        return next;
      });
    }
  };

  // 主体图微调（基于当前图 + 修改描述）
  const handleRefineMain = async () => {
    if (!selectedProp || !selectedProp.imageUrl) return;
    if (!refinePrompt.trim()) return toast.warning("请填写修改描述");
    const propId = selectedProp.id;
    setRefiningIds(prev => new Set(prev).add(propId));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_PROP_VARIANT", {
        originalPrompt: selectedProp.description || "",
        baseImageUrl: selectedProp.imageUrl,
        variantPrompt: refinePrompt,
        name: selectedProp.name,
        projectId,
        imageQualityTier,
        workshopAssetType: "prop",
        workshopAssetId: propId,
        workshopTarget: "main",
      }, { styleAnchor: currentStyle, projectId, source: "prop_variant" });
      stopAssetPolling(propId);
      const latest = getPropById(propId) || selectedProp;
      await savePropAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchProps();
      setShowRefinePanel(false);
      setRefinePrompt("");
      toast.success("微调完成！");
    } catch (e) {
      const recovered = await recoverPropImageAfterTaskError(propId, selectedProp.imageUrl);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("微调失败: " + e.message);
    }
    finally {
      setRefiningIds(prev => {
        const next = new Set(prev);
        next.delete(propId);
        return next;
      });
    }
  };

  // 主体图上传替换
  const handleReplaceMainImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedProp) return;
    const propId = selectedProp.id;
    setReplacingMainIds(prev => new Set(prev).add(propId));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const newImageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      stopAssetPolling(propId);
      await savePropAsset({ ...clearVideoAssetBinding(selectedProp), imageUrl: newImageUrl });
      await fetchProps();
      toast.success("图片已替换");
    } catch (err) { toast.error("上传失败: " + err.message); }
    finally {
      setReplacingMainIds(prev => {
        const next = new Set(prev);
        next.delete(propId);
        return next;
      });
      if (replaceMainInputRef.current) replaceMainInputRef.current.value = "";
    }
  };

  // 删除变体
  const handleDeleteVariant = async (idx) => {
    if (!selectedProp) return;
    const updatedVariations = propVariations.filter((_, i) => i !== idx);
    try {
      await savePropAsset({ ...selectedProp, variations: updatedVariations });
      await fetchProps();
      toast.success(propVariations[idx]?.imageUrl ? "变体已移入回收站，可联系管理员恢复" : "未生成图片的变体草稿已移除");
    } catch (e) { toast.error("删除失败: " + e.message); }
    finally { setDeleteVariantIndex(null); }
  };

  // 重新生成变体
  const handleRegenerateVariant = async (idx) => {
    const variant = propVariations[idx];
    if (!selectedProp || !variant) return;
    if (!variant.description && !variant.title) return toast.warning("该变体没有描述，无法重新生成");
    const propId = selectedProp.id;
    const variantKey = `${propId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    try {
      const variantDesc = variant.description || variant.title;
      const basePrompt = selectedProp.description || "";
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_PROP_VARIANT", {
        baseImageUrl: selectedProp.imageUrl,
        variantPrompt: variantDesc,
        originalPrompt: basePrompt,
        name: variant.title || selectedProp.name,
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "prop_variant" });
      const updatedVariations = propVariations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(propId);
      await savePropAsset({ ...selectedProp, variations: updatedVariations });
      await fetchProps();
      toast.success("变体已重新生成！");
    } catch (e) { toast.error("生成失败: " + e.message); }
    finally {
      setRegeneratingVariantKeys(prev => {
        const next = new Set(prev);
        next.delete(variantKey);
        return next;
      });
    }
  };

  // 生成变体
  const handleGenerateVariant = async () => {
    if (!selectedProp) return;
    if (!variantName.trim()) return toast.warning("请填写变体名称");
    if (variantMode === "text" && !variantPrompt.trim()) return toast.warning("请填写变体描述");
    if (variantMode === "image" && !variantRefImage) return toast.warning("请上传参考图");
    const propId = selectedProp.id;
    setGeneratingVariantIds(prev => new Set(prev).add(propId));
    try {
      const basePrompt = selectedProp.description || "";
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_PROP_VARIANT", {
        baseImageUrl: selectedProp.imageUrl,
        variantPrompt: variantMode === "text" ? variantPrompt : (variantRefLabel || "参考图片进行变体生成"),
        originalPrompt: basePrompt,
        ...(variantMode === "image" && { refImageUrl: variantRefImage }),
        name: variantName.trim(),
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "prop_variant" });
      const updatedVariations = [...propVariations, {
        title: variantName.trim(),
        imageUrl: newImageUrl,
        description: variantMode === "text" ? variantPrompt : (variantRefLabel || "参考图变体"),
      }];
      await savePropAsset({ ...selectedProp, variations: updatedVariations });
      await fetchProps();
      setShowVariantPanel(false);
      setVariantName(""); setVariantPrompt(""); setVariantRefImage(null); setVariantRefLabel("");
      toast.success("变体生成成功！");
    } catch (e) { toast.error("生成失败: " + e.message); }
    finally {
      setGeneratingVariantIds(prev => {
        const next = new Set(prev);
        next.delete(propId);
        return next;
      });
    }
  };

  // 变体微调（基于主体图 + 变体图 + 修改描述）
  const handleRefineVariant = async (idx) => {
    const variant = propVariations[idx];
    if (!selectedProp || !variant?.imageUrl) return;
    if (!variantRefinePrompt.trim()) return toast.warning("请填写修改描述");
    const propId = selectedProp.id;
    const variantKey = `${propId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_PROP_VARIANT", {
        originalPrompt: selectedProp.description || "",
        baseImageUrl: variant.imageUrl,
        variantPrompt: variantRefinePrompt,
        name: variant.title || selectedProp.name,
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "prop_variant" });
      const updatedVariations = propVariations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(propId);
      await savePropAsset({ ...selectedProp, variations: updatedVariations });
      await fetchProps();
      setShowVariantRefinePanel(false);
      setVariantRefinePrompt("");
      toast.success("变体微调完成！");
    } catch (e) { toast.error("微调失败: " + e.message); }
    finally {
      setRegeneratingVariantKeys(prev => { const next = new Set(prev); next.delete(variantKey); return next; });
    }
  };

  // 直接上传新变体，不触发图片生成或素材入库。
  const handleUploadNewVariant = async ({ assetId, file, title, description }) => {
    const uploadProjectId = projectId;
    const saved = await uploadWorkshopVariant({
      assetId,
      projectId: uploadProjectId,
      file,
      title,
      description,
      uploadFile: async (uploadFile) => {
        const formData = new FormData();
        formData.append("file", uploadFile);
        return authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      },
      getAsset: (id) => isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId) ? getPropById(id) : null,
      saveAsset: (asset, rebase) => savePropAsset(asset, {
        rebase,
        shouldApply: () => isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId),
      }),
      onBeforeSave: () => stopAssetPolling(assetId),
    });
    if (isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId)) await fetchProps();
    return saved;
  };

  // 变体替换图片
  const handleReplaceVariantImage = async (e, idx) => {
    const input = e.target;
    const file = input.files[0];
    const originalVariant = propVariations[idx];
    if (!file || !selectedProp || !originalVariant) return;
    const propId = selectedProp.id;
    const uploadProjectId = projectId;
    const originalImageUrl = originalVariant.imageUrl;
    const originalTitle = originalVariant.title;
    const originalDescription = originalVariant.description;
    const variantKey = `${propId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    const formData = new FormData();
    formData.append("file", file);
    try {
      validateVariantImageFile(file);
      const newImageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      const latestProp = getPropById(propId);
      if (!isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId)
        || !latestProp || (latestProp.projectId != null && !isSameWorkshopAssetId(latestProp.projectId, uploadProjectId))) {
        throw new Error("道具已删除或项目已切换，请在原项目重新上传");
      }
      const latestVariant = latestProp.variations?.[idx];
      if (!latestVariant || latestVariant.imageUrl !== originalImageUrl
        || latestVariant.title !== originalTitle || latestVariant.description !== originalDescription) {
        throw new Error("该变体已修改或删除，请重新选择要上传的变体");
      }
      const updatedVariations = latestProp.variations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(propId);
      await savePropAsset({ ...latestProp, variations: updatedVariations }, {
        shouldApply: () => isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId),
        rebase: currentAsset => {
          const currentVariant = currentAsset.variations?.[idx];
          if (!currentVariant || currentVariant.imageUrl !== originalImageUrl
            || currentVariant.title !== originalTitle || currentVariant.description !== originalDescription) {
            throw new Error("该变体已修改或删除，请重新选择要上传的变体");
          }
          return {
            ...currentAsset,
            variations: currentAsset.variations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v),
          };
        },
      });
      if (isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId)) await fetchProps();
      toast.success(originalImageUrl ? "变体图片已替换" : "变体图片已上传");
    } catch (err) { toast.error("上传失败: " + err.message); }
    finally {
      setRegeneratingVariantKeys(prev => { const next = new Set(prev); next.delete(variantKey); return next; });
      if (input) input.value = "";
    }
  };

  // 变体单独入库
  const handleUploadVariantToAssets = async (idx) => {
    if (!assetIngestSupported) return toast.info("当前项目视频服务不支持素材入库");
    const variant = propVariations[idx];
    if (!selectedProp || !variant?.imageUrl) return toast.warning("没有变体图，无法入库");
    const propId = selectedProp.id;
    setUploadingAssetIds(prev => new Set(prev).add(`${propId}-v${idx}`));
    try {
      const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/create`, {
        method: "POST",
        body: JSON.stringify({ projectId, url: variant.imageUrl, name: `${selectedProp.name || "道具"}-${variant.title || `变体${idx + 1}`}`, assetType: "Image", typeCode: "prop_image", source: "asset", sourceAssetId: propId, sourceDetail: "prop_generator_variant" }),
      });
      const applied = applyAssetCreateResult(variant, res, assetProvider);
      if (applied.accepted) {
        const updatedVariations = propVariations.map((v, i) => i === idx ? applied.target : v);
        const updated = { ...selectedProp, variations: updatedVariations };
        await savePropAsset(updated, {
          rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
        });
        await fetchProps();
        pollAssetStatus(updated);
        toast.success(`${assetLibraryLabel(applied.provider)}变体入库已提交`);
      }
    } catch (e) { toast.error("入库失败: " + e.message); }
    finally {
      setUploadingAssetIds(prev => { const next = new Set(prev); next.delete(`${propId}-v${idx}`); return next; });
    }
  };

  // 保存变体描述
  const handleSaveVariantDescription = async (idx) => {
    if (!selectedProp) return;
    try {
      const updatedVariations = propVariations.map((v, i) => i === idx ? { ...v, description: editVariantDescText } : v);
      await savePropAsset({ ...selectedProp, variations: updatedVariations });
      await fetchProps();
      setEditingVariantDesc(false);
      toast.success("变体描述已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 上传变体参考图
  const handleUploadVariantRef = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      setVariantRefImage(imageUrl);
    } catch (err) { toast.error("上传失败: " + err.message); }
    finally { if (variantRefInputRef.current) variantRefInputRef.current.value = ""; }
  };

  // 上传素材到 GuaVideo 素材库（主体图 + 所有变体），保存 assetId 和状态
  const handleUploadToAssets = async () => {
    if (!assetIngestSupported) return toast.info("当前项目视频服务不支持素材入库");
    if (!selectedProp?.imageUrl) return toast.warning("没有主体图，无法上传素材库");
    const propId = selectedProp.id;
    setUploadingAssetIds(prev => new Set(prev).add(propId));
    try {
      const updated = { ...selectedProp };
      try {
        const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/create`, {
          method: "POST",
          body: JSON.stringify({ projectId, url: updated.imageUrl, name: updated.name || "道具主体", assetType: "Image", typeCode: "prop_image", source: "asset", sourceAssetId: propId, sourceDetail: "prop_generator_main" }),
        });
        const applied = applyAssetCreateResult(updated, res, assetProvider);
        if (applied.accepted) {
          Object.assign(updated, applied.target);
        }
      } catch (e) { toast.error("主体图入库失败: " + e.message); }

      const variations = [...(updated.variations || [])];
      for (let i = 0; i < variations.length; i++) {
        const v = variations[i];
        if (!v.imageUrl) continue;
        try {
          const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/create`, {
            method: "POST",
            body: JSON.stringify({ projectId, url: v.imageUrl, name: `${updated.name || "道具"}-${v.title || `变体${i + 1}`}`, assetType: "Image", typeCode: "prop_image", source: "asset", sourceAssetId: propId, sourceDetail: "prop_generator_variant" }),
          });
          const applied = applyAssetCreateResult(v, res, assetProvider);
          if (applied.accepted) {
            variations[i] = applied.target;
          }
        } catch { /* skip */ }
      }
      updated.variations = variations;

      await savePropAsset(updated, {
        rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
      });
      await fetchProps();
      const meta = getEffectiveAssetMeta(updated, assetProvider);
      toast.success(`${assetLibraryLabel(meta.provider)}入库已提交，等待处理`);
      pollAssetStatus(updated);
    } catch (e) { toast.error("入库失败: " + e.message); }
    finally {
      setUploadingAssetIds(prev => { const next = new Set(prev); next.delete(propId); return next; });
    }
  };

  const pollAssetStatus = (entity) => {
    if (!assetIngestSupported) return;
    const entityId = entity.id;
    if (pollingIdsRef.current.has(entityId)) return;
    const token = createLocalToken();
    pollingIdsRef.current.set(entityId, token);

    const ids = [];
    const mainMeta = getEffectiveAssetMeta(entity, assetProvider);
    const mainAssetId = mainMeta.assetId;
    const mainStatus = mainMeta.status;
    if (mainAssetId && mainStatus === "Processing") {
      ids.push({ assetId: mainAssetId, provider: mainMeta.provider, target: "main", url: entity.imageUrl });
    }
    (entity.variations || []).forEach((v, i) => {
      const meta = getEffectiveAssetMeta(v, assetProvider);
      const assetId = meta.assetId;
      const status = meta.status;
      if (assetId && status === "Processing") {
        ids.push({ assetId, provider: meta.provider, target: "variant", index: i, url: v.imageUrl });
      }
    });
    if (ids.length === 0) { stopAssetPolling(entityId); return; }

    const poll = async () => {
      if (pollingIdsRef.current.get(entityId) !== token) return;
      let allDone = true;
      const updated = { ...entity };
      const variations = [...(updated.variations || [])];
      for (const item of ids) {
        if (pollingIdsRef.current.get(entityId) !== token) return;
        try {
          const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/status`, {
            method: "POST",
            body: JSON.stringify({
              projectId: projectId ? Number(projectId) : undefined,
              url: item.url,
              assetId: item.assetId,
            }),
          });
          if (pollingIdsRef.current.get(entityId) !== token) return;
          const currentTarget = item.target === "main" ? updated : variations[item.index];
          const applied = applyAssetStatusResult(currentTarget, res, assetProvider);
          if (!applied.accepted) {
            allDone = false;
            continue;
          }
          const status = applied.status;
          if (item.target === "main") {
            Object.assign(updated, applied.target);
          } else {
            variations[item.index] = applied.target;
          }
          if (status === "Processing") allDone = false;
        } catch { allDone = false; }
      }
      updated.variations = variations;
      try {
        if (pollingIdsRef.current.get(entityId) !== token) return;
        await savePropAsset(updated, {
          shouldApply: () => pollingIdsRef.current.get(entityId) === token,
          rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
        });
        if (pollingIdsRef.current.get(entityId) !== token) return;
      } catch { /* ignore */ }
      if (!allDone) {
        scheduleAssetPoll(
          entityId,
          token,
          poll,
          3000,
        );
      } else {
        if (pollingIdsRef.current.get(entityId) === token) {
          stopAssetPolling(entityId);
        }
        fetchProps();
      }
    };
    scheduleAssetPoll(
      entityId,
      token,
      poll,
      3000,
    );
  };

  // 保存别名
  const handleSaveAlias = async () => {
    if (!selectedProp) return;
    try {
      const aliasArray = editAliasText.split(/[,，]/).map(s => s.trim()).filter(Boolean);
      await savePropAsset({ ...selectedProp, aliases: JSON.stringify(aliasArray) });
      await fetchProps();
      setEditingAlias(false);
      toast.success("别名已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 保存描述
  const handleSaveDescription = async () => {
    if (!selectedProp) return;
    try {
      await savePropAsset({ ...selectedProp, description: editDescriptionText });
      await fetchProps();
      setEditingDescription(false);
      toast.success("描述已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 删除道具
  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await authenticatedFetch(`${API_BASE}/api/props/${deleteTargetId}`, { method: "DELETE" });
      setProps(prev => prev.filter(p => p.id !== deleteTargetId));
      if (selectedPropId === deleteTargetId) setSelectedPropId(null);
      toast.success("已移入回收站，可联系管理员恢复");
    } catch (e) { toast.error("删除失败: " + e.message); } finally { setDeleteTargetId(null); }
  };

  const handleRename = async (name) => {
    if (!renameTarget?.id) return;
    const id = renameTarget.id;
    setRenamingId(id);
    try {
      const updated = await authenticatedFetch(`${API_BASE}/api/props/${id}/name`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      setProps(prev => prev.map(p => p.id === id ? updated : p));
      toast.success("道具已更名");
      setRenameTarget(null);
    } catch (e) {
      toast.error("更名失败: " + e.message);
    } finally {
      setRenamingId(null);
    }
  };

  return (
    <div className="workshop-shell w-full h-full flex flex-col font-sans" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-primary)' }}>

      {/* 大图预览 */}
      {previewImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-8 animate-in fade-in" onClick={() => setPreviewImage(null)} style={{ backgroundColor: 'var(--overlay-heavy)', backdropFilter: 'blur(12px)' }}>
          <button className="absolute top-6 right-6 p-2 rounded-full" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)' }}><X size={24} /></button>
          <img src={previewImage} className="max-w-full max-h-full object-contain rounded-xl animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* 顶部栏 */}
      <div className="workshop-header h-14 border-b flex items-center justify-between px-6 shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
        <h2 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <span className="workshop-title-icon p-1.5 rounded-lg"><WorkshopArtwork kind="prop" size={16} /></span>
          道具工坊
        </h2>
        <div className="workshop-toolbar flex items-center gap-2">
          <ModelArkLibraryToolbar registry={modelArkRegistry} />
          <AppSelect
            value={imageQualityTier}
            onChange={(e) => handleImageQualityTierChange(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg text-xs font-bold outline-none"
            title="图片清晰度档位"
            style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}
          >
            {availableImageQualityTierOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </AppSelect>
          {props.filter(p => p.description && p.description !== "道具图" && !p.imageUrl).length > 0 && (
            <button
              onClick={handleBatchGenerate}
              disabled={batchGenerating}
              className="workshop-primary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs"
              style={{ background: batchGenerating ? 'var(--color-bg-tertiary)' : 'var(--color-accent-primary)', color: 'white', opacity: batchGenerating ? 0.7 : 1 }}
            >
              {batchGenerating ? <><Loader2 size={13} className="animate-spin" /> {batchProgress.done}/{batchProgress.total}</> : <><Wand2 size={13} /> 一键生成</>}
            </button>
          )}
          <button
            onClick={() => { setIsCreating(true); setIsGenerating(false); setIsUploading(false); setNewPropName(''); setNewPropPrompt(''); setNewPropAliases(''); setReferenceImages([]); }}
            className="workshop-primary-action flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-bold text-xs"
            style={{ background: 'var(--color-accent-primary)', color: 'white' }}
          >
            <Plus size={13} /> 新建道具
          </button>
        </div>
      </div>

      {/* 主体区域：左右分栏 */}
      <div className="workshop-body flex-1 flex min-h-0">

        {/* 左侧：道具列表 */}
        <div className="workshop-sidebar w-56 border-r overflow-y-auto custom-scrollbar p-3 space-y-2" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
          {props.map(item => (
            <div
              key={item.id}
              onClick={() => setSelectedPropId(item.id)}
              className="workshop-asset-card group relative rounded-lg overflow-hidden cursor-pointer"
              data-selected={selectedPropId === item.id}
            >
              <div className="aspect-square overflow-hidden" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                <img src={item.imageUrl} className="w-full h-full object-contain p-1" alt={item.name} />
              </div>
              <div className="workshop-asset-caption p-2">
                <h3 className="text-xs font-bold truncate" style={{ color: 'var(--color-text-primary)' }}>{item.name}</h3>
                {item.variations && item.variations.length > 0 && (
                  <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{item.variations.length} 个变体</p>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setRenameTarget(item); }}
                className="workshop-card-action absolute top-1.5 left-1.5 p-1 rounded-md"
                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                title="更名"
                aria-label={`更名 ${item.name || "道具"}`}
              >
                {renamingId === item.id ? <Loader2 size={11} className="animate-spin" /> : <Edit2 size={11} />}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setDeleteTargetId(item.id); }}
                className="workshop-card-action absolute top-1.5 right-1.5 p-1 rounded-md"
                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                aria-label={`删除道具 ${item.name || '未命名道具'}`}
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          {props.length === 0 && (
            <div className="workshop-list-empty flex flex-col items-center justify-center py-16">
              <WorkshopArtwork kind="prop" size={28} className="mb-2" />
              <p className="text-2xs text-center" style={{ color: 'var(--color-text-muted)' }}>暂无道具<br/>点击右上角新建</p>
            </div>
          )}
        </div>

        {/* 右侧：详情面板 */}
        <div className="workshop-detail flex-1 overflow-y-auto custom-scrollbar">
          {selectedProp ? (
            <div className="workshop-detail-content p-6 max-w-6xl mx-auto space-y-5">

              {/* 主体图区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-4">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>主体图</h3>
                  <div className="workshop-section-actions flex items-center gap-2">
                    <input type="file" ref={replaceMainInputRef} className="hidden" accept="image/*" onChange={handleReplaceMainImage} />
                    <button
                      onClick={() => replaceMainInputRef.current?.click()}
                      disabled={isReplacingMain}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                    >
                      {isReplacingMain ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                      {isReplacingMain ? '上传中...' : '替换'}
                    </button>
                    <button
                      onClick={handleRegenerateMain}
                      disabled={isRegeneratingMain}
                      className="workshop-primary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium disabled:opacity-50"
                      style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                    >
                      {isRegeneratingMain ? <Loader2 size={10} className="animate-spin" /> : <Wand2 size={10} />}
                      重新生成
                    </button>
                    {selectedProp.imageUrl && (
                      <button
                        onClick={() => { setShowRefinePanel(v => !v); setRefinePrompt(""); }}
                        disabled={isRefiningMain}
                        className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                        data-active={showRefinePanel} style={{
                          backgroundColor: showRefinePanel ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}20`,
                          color: showRefinePanel ? 'white' : theme.colors.accentPrimary,
                          border: `1px solid ${theme.colors.accentPrimary}`
                        }}
                      >
                        {isRefiningMain ? <Loader2 size={10} className="animate-spin" /> : <Edit2 size={10} />}
                        微调
                      </button>
                    )}
                    <ModelArkImageLibraryStatus registry={modelArkRegistry} url={selectedProp.imageUrl} />
                    {assetIngestSupported && selectedProp.imageUrl && (() => {
                      const meta = getEffectiveAssetMeta(selectedProp, assetProvider);
                      const status = meta.status;
                      const isUploading = uploadingAssetIds.has(selectedProp.id);
                      const isProcessing = status === "Processing";
                      const isReady = isAssetReadyStatus(status);
                      const isFailed = status === "Failed";
                      const disabled = isUploading || isProcessing || isReady;
                      return (
                        <button
                          onClick={handleUploadToAssets}
                          disabled={disabled}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-60"
                          style={{
                            backgroundColor: isReady ? `${theme.colors.success}20` : isFailed ? `${theme.colors.error}20` : `${theme.colors.accentPrimary}20`,
                            color: isReady ? theme.colors.success : isFailed ? theme.colors.error : theme.colors.accentPrimary,
                            border: `1px solid ${isReady ? theme.colors.success : isFailed ? theme.colors.error : theme.colors.accentPrimary}`,
                          }}
                          title={meta.assetId ? `${assetLibraryLabel(meta.provider)}：${meta.assetId}` : `提交到${assetLibraryLabel(meta.provider)}`}
                        >
                          {isUploading || isProcessing ? <Loader2 size={10} className="animate-spin" /> : isReady ? <Check size={10} /> : <PackagePlus size={10} />}
                          {isUploading ? '入库中...' : assetStatusLabel(status, isFailed ? '入库失败' : '素材入库')}
                        </button>
                      );
                    })()}
                  </div>
                </div>

                {/* 微调面板 */}
                {showRefinePanel && selectedProp.imageUrl && (
                  <div className="mb-4 p-4 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}` }}>
                    <label className="text-2xs font-medium block" style={{ color: 'var(--color-text-muted)' }}>修改描述（基于当前图微调）</label>
                    <textarea
                      className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                      style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                      rows={2}
                      value={refinePrompt}
                      onChange={e => setRefinePrompt(e.target.value)}
                      placeholder="例如：加一些锈迹、改成金色、增加发光效果..."
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setShowRefinePanel(false); setRefinePrompt(""); }}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >取消</button>
                      <button
                        onClick={handleRefineMain}
                        disabled={isRefiningMain || !refinePrompt.trim()}
                        className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        {isRefiningMain ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                        {isRefiningMain ? "微调中..." : "开始微调"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {selectedProp.imageUrl ? (
                    <div className="workshop-preview relative group aspect-square max-w-md mx-auto rounded-lg overflow-hidden cursor-zoom-in" aria-busy={isRegeneratingMain || isRefiningMain} style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(selectedProp.imageUrl)}>
                      <img src={selectedProp.imageUrl} className="w-full h-full object-contain p-4" alt={selectedProp.name} />
                      {(isRegeneratingMain || isRefiningMain) && (
                        <div className="workshop-preview-status" role="status">
                          <span className="workshop-preview-status-dot" aria-hidden="true" />
                          <span>{isRefiningMain ? '微调中' : '重新生成中'} · 显示上一版</span>
                        </div>
                      )}
                        <>
                          {/* 下载按钮 */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const link = document.createElement('a');
                              link.href = selectedProp.imageUrl;
                              link.download = `${selectedProp.name || '道具'}.png`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="workshop-preview-action absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: 'var(--overlay-medium)' }}
                            title="下载图片"
                          >
                            <Download size={16} className="text-white" />
                          </button>
                        </>
                    </div>
                  ) : (
                    <div className="aspect-square max-w-md mx-auto rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-3" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-overlay)' }}>
                      <Sparkles size={32} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>尚未生成道具图</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleRegenerateMain}
                          disabled={isRegeneratingMain || !selectedProp.description}
                          className="workshop-primary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50"
                          style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                        >
                          {isRegeneratingMain ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                          AI 生成
                        </button>
                        <button
                          onClick={() => replaceMainInputRef.current?.click()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                        >
                          <Upload size={12} /> 上传图片
                        </button>
                      </div>
                      {!selectedProp.description && (
                        <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>（需要先填写道具描述才能 AI 生成）</p>
                      )}
                    </div>
                  )}

                  {/* 基本信息 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>道具名称</label>
                      <div className="text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>{selectedProp.name}</div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>别名（智能匹配）</label>
                        {!editingAlias && (
                          <button onClick={() => { setEditingAlias(true); setEditAliasText(propAliases.join(', ')); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                            <Edit2 size={9} /> 编辑
                          </button>
                        )}
                      </div>
                      {editingAlias ? (
                        <div className="flex gap-1.5">
                          <input
                            className="flex-1 rounded-md px-2 py-1.5 text-xs outline-none"
                            style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)' }}
                            value={editAliasText}
                            onChange={e => setEditAliasText(e.target.value)}
                            placeholder="激光枪, 光剑..."
                          />
                          <button onClick={handleSaveAlias} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                          <button onClick={() => setEditingAlias(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {propAliases.length > 0
                            ? propAliases.map((a, i) => <span key={i} className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>{a}</span>)
                            : <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无别名</span>
                          }
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>原始描述（AI 生成提示词）</label>
                      {!editingDescription && (
                        <button onClick={() => { setEditingDescription(true); setEditDescriptionText(selectedProp.description || ""); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                          <Edit2 size={9} /> 编辑
                        </button>
                      )}
                    </div>
                    {editingDescription ? (
                      <div className="space-y-2">
                        <textarea
                          rows={3}
                          className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                          style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)' }}
                          value={editDescriptionText}
                          onChange={e => setEditDescriptionText(e.target.value)}
                          placeholder="描述材质、光泽、用途..."
                        />
                        <div className="flex justify-end gap-1.5">
                          <button onClick={handleSaveDescription} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                          <button onClick={() => setEditingDescription(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs p-3 rounded-lg leading-relaxed" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}>
                        {selectedProp.description || "无描述"}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 参考图展示区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-3">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>参考图</h3>
                  {!editingDetailRefs && (
                    <button
                      onClick={handleStartEditDetailRefs}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                    >
                      <Edit2 size={10} /> 编辑
                    </button>
                  )}
                </div>

                {editingDetailRefs ? (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      {detailRefImages.map((ref, idx) => (
                        <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                          <div className="flex gap-3">
                            <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 cursor-zoom-in group" style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(ref.url)}>
                              <img src={ref.url} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveDetailRefImage(idx); }}
                                className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                              >
                                <X size={10} />
                              </button>
                            </div>
                            <div className="flex-1 space-y-1.5">
                              <div className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>参考图{idx + 1} - 用途说明</div>
                              <input
                                type="text"
                                className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                                style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                                placeholder="例如：参考这个材质、参考这个形状"
                                value={ref.label}
                                onChange={(e) => handleUpdateDetailRefLabel(idx, e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      {detailRefImages.length < maxReferenceImages && (
                        <>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            ref={el => detailRefInputRefs.current[0] = el}
                            onChange={handleUploadDetailRefImage}
                          />
                          <button
                            onClick={() => detailRefInputRefs.current[0]?.click()}
                            disabled={uploadingDetailRefIndex !== null}
                            className="w-full py-2 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 workshop-control-feedback hover:border-solid disabled:opacity-50"
                            style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                          >
                            {uploadingDetailRefIndex !== null ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <>
                                <Plus size={14} />
                                <span className="text-xs">添加参考图</span>
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                    <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                      用途说明会告诉 AI 参考图的哪个部分（材质/形状/细节等）
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingDetailRefs(false)}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleSaveDetailRefs}
                        className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        <Check size={11} /> 保存
                      </button>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const savedRefs = (() => { try { const r = JSON.parse(selectedProp.referenceImages || '[]'); return r.map(x => typeof x === 'string' ? { url: x, label: '' } : x); } catch { return []; } })();
                    if (savedRefs.length === 0) {
                      return (
                        <div className="flex flex-col items-center justify-center py-8">
                          <ImagePlus size={24} className="mb-2 opacity-20" style={{ color: 'var(--color-text-muted)' }} />
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无参考图，点击"编辑"添加</p>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-2">
                        {savedRefs.map((ref, idx) => (
                          <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                            <div className="flex gap-3">
                              <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 cursor-zoom-in" style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(ref.url)}>
                                <img src={ref.url} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                              </div>
                              <div className="flex-1">
                                <div className="text-2xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>参考图{idx + 1}</div>
                                <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{ref.label || "无用途说明"}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>

              {/* 变体区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-4">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>道具变体</h3>
                  <div className="workshop-section-actions flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setShowVariantPanel(!showVariantPanel)}
                      className="workshop-secondary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs workshop-control-feedback"
                      data-active={showVariantPanel} style={{ backgroundColor: showVariantPanel ? 'var(--color-bg-tertiary)' : theme.colors.accentPrimary, color: showVariantPanel ? 'var(--color-text-muted)' : 'white' }}
                    >
                      {showVariantPanel ? <X size={11} /> : <Plus size={11} />}
                      {showVariantPanel ? '取消' : '生成变体'}
                    </button>
                    <WorkshopVariantUpload
                      key={`${projectId}-${selectedProp.id}`}
                      assetId={selectedProp.id}
                      assetName={selectedProp.name}
                      assetLabel="道具"
                      disabled={isGeneratingVariant}
                      onUpload={handleUploadNewVariant}
                    />
                  </div>
                </div>

                {/* 变体生成面板 */}
                {showVariantPanel && (
                  <div className="mb-4 p-4 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}` }}>
                    <div>
                      <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>变体名称</label>
                      <input
                        className="w-full rounded-md px-3 py-2 text-xs outline-none"
                        style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                        value={variantName}
                        onChange={e => setVariantName(e.target.value)}
                        placeholder="例如：损坏版"
                      />
                    </div>

                    {/* 模式切换 */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setVariantMode("text")}
                        className="workshop-secondary-action flex-1 py-2 rounded-md text-xs font-bold workshop-control-feedback"
                        data-active={variantMode === "text"} style={{ backgroundColor: variantMode === "text" ? theme.colors.accentPrimary : 'var(--color-bg-tertiary)', color: variantMode === "text" ? 'white' : 'var(--color-text-muted)' }}
                      >
                        文字描述
                      </button>
                      <button
                        onClick={() => setVariantMode("image")}
                        className="workshop-secondary-action flex-1 py-2 rounded-md text-xs font-bold workshop-control-feedback"
                        data-active={variantMode === "image"} style={{ backgroundColor: variantMode === "image" ? theme.colors.accentPrimary : 'var(--color-bg-tertiary)', color: variantMode === "image" ? 'white' : 'var(--color-text-muted)' }}
                      >
                        参考图
                      </button>
                    </div>

                    {variantMode === "text" ? (
                      <div>
                        <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>变体描述（基于主体生成）</label>
                        <textarea
                          rows={2}
                          className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                          style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                          value={variantPrompt}
                          onChange={e => setVariantPrompt(e.target.value)}
                          placeholder="例如：破损的、沾满灰尘的版本"
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>上传参考图</label>
                          <input type="file" ref={variantRefInputRef} className="hidden" accept="image/*" onChange={handleUploadVariantRef} />
                          {variantRefImage ? (
                            <div className="flex gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                              <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 group" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                                <img src={variantRefImage} className="w-full h-full object-cover" alt="参考图" />
                                <button onClick={() => { setVariantRefImage(null); setVariantRefLabel(""); }} className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>
                                  <X size={10} />
                                </button>
                              </div>
                              <div className="flex-1 space-y-1.5">
                                <div className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>用途说明</div>
                                <input
                                  type="text"
                                  className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                                  style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                                  placeholder="例如：参考这个材质、参考这个形状"
                                  value={variantRefLabel}
                                  onChange={(e) => setVariantRefLabel(e.target.value)}
                                />
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => variantRefInputRef.current?.click()}
                              className="w-full py-6 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 workshop-control-feedback hover:border-solid"
                              style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                            >
                              <ImagePlus size={24} />
                              <span className="text-xs">点击上传参考图</span>
                            </button>
                          )}
                        </div>
                        <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                          输出仍为标准道具图，仅按参考图变化
                        </p>
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => { setShowVariantPanel(false); setVariantName(""); setVariantPrompt(""); setVariantRefImage(null); setVariantRefLabel(""); }}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleGenerateVariant}
                        disabled={isGeneratingVariant || !variantName.trim() || (variantMode === "text" && !variantPrompt.trim()) || (variantMode === "image" && !variantRefImage)}
                        className="workshop-primary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        {isGeneratingVariant ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                        {isGeneratingVariant ? "生成中..." : "生成变体"}
                      </button>
                    </div>
                  </div>
                )}

                {/* 变体列表（手风琴） */}
                <div className="space-y-2">
                  {propVariations.map((v, vi) => {
                    const isExpanded = expandedVariantIndex === vi;
                    const isRegenerating = getRegeneratingVariantIndex(vi);
                    const vMeta = getEffectiveAssetMeta(v, assetProvider);
                    const vStatus = vMeta.status;
                    const isVUploading = uploadingAssetIds.has(`${selectedProp.id}-v${vi}`);
                    const isVProcessing = vStatus === "Processing";
	                    const isVReady = isAssetReadyStatus(vStatus);
	                    const isVFailed = vStatus === "Failed";

                    return (
                      <div key={vi} data-expanded={isExpanded} className="workshop-variant-card rounded-lg overflow-hidden" style={{ border: `1px solid ${isExpanded ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`, backgroundColor: 'var(--color-bg-primary)' }}>
                        {/* 折叠头 */}
                        <div
                          className="workshop-variant-heading flex items-center gap-3 px-3 py-2 cursor-pointer workshop-control-feedback"
                          style={{ backgroundColor: isExpanded ? `${theme.colors.accentPrimary}10` : 'transparent' }}
                          onClick={() => {
                            setExpandedVariantIndex(isExpanded ? null : vi);
                            setShowVariantRefinePanel(false);
                            setVariantRefinePrompt("");
                            setEditingVariantDesc(false);
                          }}
                        >
                          <ChevronRight
                            size={14}
                            className="ui-accordion-chevron"
                            data-open={isExpanded}
                            style={{ color: isExpanded ? theme.colors.accentPrimary : 'var(--color-text-muted)' }}
                          />
                          <div className="w-16 h-7 rounded overflow-hidden shrink-0" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                            {v.imageUrl && <img src={v.imageUrl} className="w-full h-full object-cover" alt={v.title} />}
                          </div>
                          <span className="text-xs font-bold flex-1 truncate" style={{ color: 'var(--color-text-primary)' }}>{v.title}</span>
                          {isRegenerating && <Loader2 size={12} className="animate-spin" style={{ color: theme.colors.greenPrimary }} />}
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteVariantIndex(vi); }}
                            className="workshop-variant-delete p-1 rounded-md workshop-control-feedback"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>

                        {/* 展开内容 */}
                        <div className="ui-accordion-grid" data-open={isExpanded}>
                          <div className="ui-accordion-content" inert={!isExpanded} aria-hidden={!isExpanded}>
                            <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                            {/* 操作按钮行 */}
                            <div className="flex flex-wrap items-center gap-2 pt-3">
                              <input
                                type="file"
                                ref={(node) => {
                                  if (node) variantReplaceInputRefs.current.set(vi, node);
                                  else variantReplaceInputRefs.current.delete(vi);
                                }}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => handleReplaceVariantImage(e, vi)}
                              />
                              <button
                                onClick={() => variantReplaceInputRefs.current.get(vi)?.click()}
                                disabled={isRegenerating}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                              >
                                <Upload size={10} /> {v.imageUrl ? '替换图片' : '上传图片'}
                              </button>
                              <button
                                onClick={() => handleRegenerateVariant(vi)}
                                disabled={isRegenerating}
                                className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                              >
                                {isRegenerating ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                重新生成
                              </button>
                              {v.imageUrl && (
                                <button
                                  onClick={() => { setShowVariantRefinePanel(p => !p); setVariantRefinePrompt(""); }}
                                  disabled={isRegenerating}
                                  className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                  data-active={showVariantRefinePanel} style={{
                                    backgroundColor: showVariantRefinePanel ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}20`,
                                    color: showVariantRefinePanel ? 'white' : theme.colors.accentPrimary,
                                    border: `1px solid ${theme.colors.accentPrimary}`
                                  }}
                                >
                                  <Edit2 size={10} /> 微调
                                </button>
                              )}
                              <ModelArkImageLibraryStatus registry={modelArkRegistry} url={v.imageUrl} />
                              {assetIngestSupported && v.imageUrl && (() => {
                                const disabled = isVUploading || isVProcessing || isVReady;
                                return (
                                  <button
                                    onClick={() => handleUploadVariantToAssets(vi)}
                                    disabled={disabled}
                                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-60"
                                    style={{
                                      backgroundColor: isVReady ? `${theme.colors.success}20` : isVFailed ? `${theme.colors.error}20` : `${theme.colors.accentPrimary}20`,
                                      color: isVReady ? theme.colors.success : isVFailed ? theme.colors.error : theme.colors.accentPrimary,
                                      border: `1px solid ${isVReady ? theme.colors.success : isVFailed ? theme.colors.error : theme.colors.accentPrimary}`,
                                    }}
                                  >
                                    {isVUploading || isVProcessing ? <Loader2 size={10} className="animate-spin" /> : isVReady ? <Check size={10} /> : <PackagePlus size={10} />}
                                    {isVUploading ? '入库中...' : assetStatusLabel(vStatus, isVFailed ? '入库失败' : '素材入库')}
                                  </button>
                                );
                              })()}
                            </div>

                            {/* 变体微调面板 */}
                            {isExpanded && showVariantRefinePanel && v.imageUrl && (
                              <div className="p-3 rounded-lg space-y-2 animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${theme.colors.accentPrimary}` }}>
                                <label className="text-2xs font-medium block" style={{ color: 'var(--color-text-muted)' }}>修改描述（基于当前变体图微调）</label>
                                <textarea
                                  ref={isExpanded ? variantRefinePromptTextareaRef : null}
                                  className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                                  style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                                  rows={2}
                                  value={variantRefinePrompt}
                                  onChange={e => {
                                    setVariantRefinePrompt(e.target.value);
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  onFocus={e => {
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  onBlur={e => {
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  placeholder="例如：换成深色版本、加上锈迹..."
                                />
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => { setShowVariantRefinePanel(false); setVariantRefinePrompt(""); }} className="px-3 py-1.5 rounded-md text-xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>取消</button>
                                  <button
                                    onClick={() => handleRefineVariant(vi)}
                                    disabled={isRegenerating || !variantRefinePrompt.trim()}
                                    className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                                    style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                                  >
                                    {isRegenerating ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                                    {isRegenerating ? "微调中..." : "开始微调"}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* 大图 */}
                            {v.imageUrl ? (
                              <div className="workshop-preview relative group aspect-square rounded-lg overflow-hidden cursor-zoom-in" aria-busy={isRegenerating} style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(v.imageUrl)}>
                                <img src={v.imageUrl} className="w-full h-full object-contain p-4" alt={v.title} />
                                {isRegenerating && (
                                  <div className="workshop-preview-status" role="status">
                                    <span className="workshop-preview-status-dot" aria-hidden="true" />
                                    <span>生成中 · 显示上一版</span>
                                  </div>
                                )}
                                  <>
                                    {/* 下载按钮 */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const link = document.createElement('a');
                                        link.href = v.imageUrl;
                                        link.download = `${selectedProp?.name || '道具'}-${v.title || '变体'}.png`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      className="workshop-preview-action absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center"
                                      style={{ backgroundColor: 'var(--overlay-medium)' }}
                                      title="下载图片"
                                    >
                                      <Download size={14} className="text-white" />
                                    </button>
                                  </>
                              </div>
                            ) : (
                              <div className="aspect-square rounded-lg border-2 border-dashed flex items-center justify-center" style={{ borderColor: 'var(--color-border-primary)' }}>
                                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无图片</p>
                              </div>
                            )}

                            {/* 变体描述 */}
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>变体描述</label>
                                {!editingVariantDesc && (
                                  <button onClick={() => { setEditingVariantDesc(true); setEditVariantDescText(v.description || ""); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                                    <Edit2 size={9} /> 编辑
                                  </button>
                                )}
                              </div>
                              {editingVariantDesc ? (
                                <div className="space-y-2">
                                  <textarea
                                    ref={isExpanded ? editVariantDescTextareaRef : null}
                                    rows={2}
                                    className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                                    style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                                    value={editVariantDescText}
                                    onChange={e => {
                                      setEditVariantDescText(e.target.value);
                                      const textarea = e.target;
                                      textarea.style.height = 'auto';
                                      textarea.style.height = textarea.scrollHeight + 'px';
                                    }}
                                    onFocus={e => {
                                      const textarea = e.target;
                                      textarea.style.height = 'auto';
                                      textarea.style.height = textarea.scrollHeight + 'px';
                                    }}
                                    onBlur={e => {
                                      const textarea = e.target;
                                      textarea.style.height = 'auto';
                                      textarea.style.height = textarea.scrollHeight + 'px';
                                    }}
                                    placeholder="描述这个变体的特征..."
                                  />
                                  <div className="flex justify-end gap-1.5">
                                    <button onClick={() => handleSaveVariantDescription(vi)} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                                    <button onClick={() => setEditingVariantDesc(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs p-2 rounded-md leading-relaxed" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                                  {v.description || "无描述"}
                                </div>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {propVariations.length === 0 && (
                    <div className="workshop-variant-empty flex flex-col items-center justify-center py-12">
                      <Sparkles size={24} className="mb-2 opacity-20" style={{ color: 'var(--color-text-muted)' }} />
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无变体，点击"生成变体"开始</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          ) : (
            <div className="workshop-empty flex flex-col items-center justify-center h-full">
              <WorkshopArtwork kind="prop" size={48} className="mb-3" />
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>请从左侧选择道具</p>
            </div>
          )}
        </div>
      </div>

      {/* 新建道具弹窗 */}
      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--overlay-medium)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>新建道具</h3>
              <button onClick={() => { setIsCreating(false); setNewPropName(""); setNewPropPrompt(""); setNewPropAliases(""); setReferenceImages([]); }} className="p-1 rounded-lg" style={{ color: 'var(--color-text-muted)' }}><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>材质风格</label>
                  <AppSelect value={currentStyle} onChange={(e) => setCurrentStyle(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}>
                    {STYLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </AppSelect>
                </div>
                <div>
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>道具名称</label>
                  <input value={newPropName} onChange={(e) => setNewPropName(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="例如：激光枪" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>别名（逗号分隔）</label>
                <input value={newPropAliases} onChange={(e) => setNewPropAliases(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="光剑, 武器" />
              </div>

              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>道具描述（用于 AI 生成）</label>
                <textarea rows={8} value={newPropPrompt} onChange={(e) => setNewPropPrompt(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none resize-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="描述材质、光泽、用途...&#10;&#10;可引用参考图，例如：&#10;参考图1的材质，参考图2的形状" />
              </div>

              {/* 参考图上传区 */}
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>
                  参考图（可选，最多{maxReferenceImages}张）
                </label>
                <div className="space-y-2">
                  {referenceImages.map((ref, idx) => (
                    <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}>
                      <div className="flex gap-3">
                        <div className="relative w-20 h-20 rounded-lg overflow-hidden shrink-0 cursor-zoom-in group" style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(ref.url)}>
                          <img src={ref.url} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveRefImage(idx); }}
                            className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                          >
                            <X size={10} />
                          </button>
                        </div>
                        <div className="flex-1 space-y-1.5">
                          <div className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>参考图{idx + 1} - 用途说明</div>
                          <input
                            type="text"
                            className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                            placeholder="例如：参考这个材质、参考这个形状"
                            value={ref.label}
                            onChange={(e) => handleUpdateRefLabel(idx, e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {referenceImages.length < maxReferenceImages && (
                    <>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        ref={el => refImageInputRefs.current[0] = el}
                        onChange={handleUploadRefImage}
                      />
                      <button
                        onClick={() => refImageInputRefs.current[0]?.click()}
                        disabled={uploadingRefIndex !== null}
                        className="w-full py-2 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 workshop-control-feedback hover:border-solid disabled:opacity-50"
                        style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                      >
                        {uploadingRefIndex !== null ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <>
                            <Plus size={14} />
                            <span className="text-xs">添加参考图</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
                <p className="text-2xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                  提示：用途说明会告诉 AI 参考图的哪个部分（材质/形状/细节等）
                </p>
              </div>

              {/* 操作按钮 */}
              <div className="grid grid-cols-2 gap-3">
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUploadNew} />
                <button onClick={handleGenerateNewMain} disabled={isGenerating} className="workshop-primary-action py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 text-xs disabled:opacity-50" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                  {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                  {isGenerating ? "创建中..." : "AI 生成"}
                </button>
                <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 text-xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}>
                  {isUploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {isUploading ? "上传中..." : "上传图片"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AssetRenameDialog
        isOpen={!!renameTarget}
        assetLabel="道具"
        currentName={renameTarget?.name}
        saving={renamingId === renameTarget?.id}
        onClose={() => setRenameTarget(null)}
        onConfirm={handleRename}
      />

      {/* 删除道具确认 */}
      <ConfirmDialog
        isOpen={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDelete}
        title="移入回收站"
        message="确定将这个道具资产移入回收站吗？移入后将不再显示，可联系管理员恢复。"
        confirmLabel="移入回收站"
        variant="danger"
      />

      {/* 删除变体确认 */}
      <ConfirmDialog
        isOpen={deleteVariantIndex !== null}
        onClose={() => setDeleteVariantIndex(null)}
        onConfirm={() => handleDeleteVariant(deleteVariantIndex)}
        title={propVariations[deleteVariantIndex]?.imageUrl ? '移入回收站' : '移除变体草稿'}
        message={propVariations[deleteVariantIndex]?.imageUrl ? '确定将这个道具变体移入回收站吗？可联系管理员恢复。' : '这个变体尚未生成图片，移除后不会进入媒体回收站。确定移除草稿吗？'}
        confirmLabel={propVariations[deleteVariantIndex]?.imageUrl ? '移入回收站' : '移除草稿'}
        variant="danger"
      />
    </div>
  );
};
export default React.memo(PropGenerator);
